import React, { useState } from "react";
import loadable from "@loadable/component";
import { useNavigate, useParams } from "react-router-dom";
import { usePDFPrintHook } from "@careinfo/layout";
import { useQueryClient } from "@tanstack/react-query";
import useGetHook from "../../customHooks/useGetHook";
import APIS from "../../constant/Endpoint";
import {
  patchApiData,
  patchApiFormData,
  postApiData,
} from "../../helpers/AxiosInstance";
import { toast } from "react-toastify";
import { tw } from "@careinfo/tailwind";
import Spinner from "../../components/Reusable/Spinner";
const EditSalesReturnComponent = loadable(
  () => import("../../components/SalesReturn/EditSalesReturn")
);

const EditSalesReturn = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { downloadPrint, isPrintLoading } = usePDFPrintHook();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openPrintPopup, setOpenPrintPopup] = useState(false);
  const [isRevertLoading, setRevertLoading] = useState(false);
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { data, isLoading: isDataLoading } = useGetHook({
    queryKey: ["sales-return", `sales-return-${id}`],
    url: `${APIS.SALES_PURCHASE_RETURN}${id}/`,
    params: {},
  });

  const { data: companyData, isLoading: isCompanyDataLoading } = useGetHook({
    url: APIS.COMPANY_PROFILE,
    queryKey: ["company-profile"],
    params: {},
  });

  const handleEdit = async (values: any, isDraft: any, setError: any) => {
    setIsLoading(true);
    const url = `${APIS.SALES_PURCHASE_RETURN}${id}/`;
    const formData = new FormData();
    formData.append("voucher_date", values?.voucher_date ?? null);
    formData.append("reference_number", values?.reference_number ?? "");
    values?.reference_date
      ? formData.append("reference_date", values?.reference_date)
      : null;
    formData.append("number_series", values?.number_series);
    formData.append("entry_number", values?.entry_number);
    formData.append("party", values?.party);
    values?.bill_number
      ? formData.append("bill_number", values?.bill_number)
      : null;
    formData.append("tax_rate", values?.tax_rate);
    formData.append("taxable_amount", values?.taxable_amount);
    formData.append("total_amount", values?.total_amount);
    formData.append("discount", values?.total_discount);
    formData.append("tax_amount", values?.vat_tax_amount);
    isDraft
      ? formData.append("status", "Draft")
      : formData.append("status", "Success");
    if (values?.items) {
      const cleanedItems = values.items.map(
        ({
          available_quantity,
          is_maintain_stock,
          is_taxable,
          last_purchase_price,
          serial_number,
          unit,
          ...rest
        }) => ({
          ...rest,
          is_taxable: is_taxable ? "true" : "false",
        })
      );
      formData.append("items", JSON.stringify(cleanedItems));
    }
    if (values?.attachment?.[0] && typeof values?.attachment !== "string") {
      formData.append("attachment", values?.attachment?.[0]);
    }
    values?.narration ? formData.append("narration", values?.narration) : null;
    try {
      const res = await patchApiFormData({ url, formData, setError });
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["sales-return"],
          refetchType: "active",
        });
        if (!isDraft) {
          setSelectedOrder(res?.data?.id);
          setOpenPrintPopup(true);
        } else {
          navigate("/accounting/sales-return");
        }
        toast.success("Updated Successfully");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    const url = `${APIS.SALES_PURCHASE_RETURN}${id}/`;
    const formData = {
      status: "Cancelled",
    };
    try {
      patchApiData({ url, formData, setError: () => {} });
    } catch (err) {
      console.error(err);
    }
  };

  const handlePrint = async () => {
    const url = APIS.BILL_PRINT;
    let formData = {
      ids: [selectedOrder],
      type: "Sales Invoice",
    };
    try {
      const res = await downloadPrint(url, formData);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRevert = async (setError) => {
    const url = `${APIS.BILL}${id}/revert/`;
    setRevertLoading(true);
    try {
      const res = await postApiData({ url, formData: {}, setError });
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["sales-return"],
          refetchType: "active",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setRevertLoading(false);
    }
  };

  return (
    <>
      <div
        className={tw`w-full flex justify-left gap-x-2 mb-4 uppercase font-extrabold items-center`}
      >
        <span className={tw`text-[#5E5757] font-bold text-xl`}>
          Edit Sales Return
        </span>
      </div>
      {isDataLoading || isCompanyDataLoading ? (
        <Spinner />
      ) : (
        <EditSalesReturnComponent
          handleEdit={handleEdit}
          isLoading={isLoading}
          data={data}
          openPrintPopup={openPrintPopup}
          companyData={companyData}
          setOpenPrintPopup={setOpenPrintPopup}
          handlePrint={handlePrint}
          isPrintLoading={isPrintLoading}
          handleRevert={handleRevert}
          handleCancel={handleCancel}
          isRevertLoading={isRevertLoading}
        />
      )}
    </>
  );
};

export default EditSalesReturn;
